import { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button, Checkbox, Modal, useToast } from '../../components/primitives';
import { buildMojeFirmaVCard, shareVCard } from '../../lib/vcard';
import { safeFileName } from '../../lib/safeFileName';
import { useAuth } from '../auth/AuthContext';
import type { MojeFirma } from './mojeFirmy';
import styles from './Ucet.module.css';

/**
 * QR vizitka vybrané firmy — naskenovat telefonem a uložit jako kontakt.
 * Volitelně s mým jménem (osobní vizitka) nebo jen na firmu.
 */
export function MojeFirmaQRModal({
  firma,
  open,
  onClose,
}: {
  firma: MojeFirma | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const [sJmenem, setSJmenem] = useState(true);
  const osoba = sJmenem ? (user?.full_name ?? '') : '';

  const vcard = useMemo(
    () => (firma ? buildMojeFirmaVCard({ ...firma, osoba }) : ''),
    [firma, osoba],
  );

  async function handleShare() {
    if (!firma) return;
    const name = safeFileName(osoba || firma.nazev) || 'kontakt';
    try {
      const res = await shareVCard(`${name}.vcf`, vcard, { title: firma.nazev });
      if (res === 'downloaded') toast.success('Vizitka stažena (.vcf).');
    } catch {
      toast.error('Vizitku se nepodařilo sdílet ani stáhnout.');
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="📱 QR vizitka"
      footer={
        <>
          <Button onClick={onClose}>Zavřít</Button>
          <Button variant="primary" onClick={() => void handleShare()} disabled={!vcard}>
            ⬇ Sdílet / stáhnout .vcf
          </Button>
        </>
      }
    >
      {firma && vcard ? (
        <div className={styles.qrWrap}>
          <div className={styles.qrBox}>
            <QRCodeSVG value={vcard} size={240} level="M" marginSize={2} />
          </div>
          <div className={styles.qrName}>{osoba || firma.nazev}</div>
          {osoba && <div className={styles.muted}>{firma.nazev}</div>}
          {user?.full_name && (
            <Checkbox
              label="Uvést moje jméno (osobní vizitka)"
              checked={sJmenem}
              onChange={(e) => setSJmenem(e.target.checked)}
            />
          )}
          <div className={styles.muted}>
            Naskenujte fotoaparátem telefonu a uložte jako kontakt. IČO, DIČ a číslo účtu jsou v poznámce
            kontaktu.
          </div>
        </div>
      ) : (
        <div className={styles.muted}>Nejdřív vyplňte údaje firmy.</div>
      )}
    </Modal>
  );
}
